import { createFileRoute, notFound } from "@tanstack/react-router"

import { AdminSectionView, AdminShell } from "@/components/admin-ui"
import { getAdminSectionFn } from "@/lib/app.functions"
import { ADMIN_SECTIONS } from "@/lib/constants"
import { requireAdminRoute } from "@/lib/route-guards"
import type { AdminSection } from "@/lib/types"

export const Route = createFileRoute("/admin/$section")({
  loader: async ({ params }) => {
    const section = ADMIN_SECTIONS.find(
      (item) => item.value === params.section
    )?.value
    if (!section) throw notFound()
    await requireAdminRoute(`/admin/${section}`)
    return getAdminSectionFn({ data: { section } })
  },
  head: ({ params }) => ({
    meta: [
      {
        title: `${sectionLabel(params.section)} · Harbor & Pine Realty`,
      },
    ],
  }),
  component: AdminSectionPage,
})

function sectionLabel(value: string) {
  return (
    ADMIN_SECTIONS.find((section) => section.value === value)?.label ??
    "Administration"
  )
}

function AdminSectionPage() {
  const { user, data } = Route.useLoaderData()
  const section = data.section
  const descriptions: Record<AdminSection, string> = {
    properties:
      "Review the complete catalogue, open any public property sheet, edit all facts and media, or archive a record.",
    inquiries:
      "Triage incoming public and member messages, record a reply, and move each item through its operational status.",
    users:
      "Review registered profiles and administer role and access state. Public demo passwords remain fixed.",
    content:
      "Create, edit, publish, draft, order, and remove home, editorial, event, company, FAQ, team, partner, and testimonial records.",
    taxonomies:
      "Maintain reusable listing attributes, keywords, property-type labels, and searchable regions.",
    settings:
      "Maintain currencies, inspect regions, and manage newsletter subscribers captured by the public site.",
  }

  return (
    <AdminShell
      user={user}
      eyebrow={`Operations desk · ${section}`}
      title={sectionLabel(section)}
      intro={descriptions[section]}
    >
      <AdminSectionView data={data} />
    </AdminShell>
  )
}
